<script setup>
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import FlowsAPI from 'dashboard/api/flows';
import SegmentAudiencePicker from 'dashboard/components-next/filter/SegmentAudiencePicker.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');

const flows = ref([]);
const loading = ref(false);

const SESSION_STATUS = {
  active: { label: 'Em andamento', cls: 'bg-n-blue-3 text-n-blue-11' },
  waiting_input: {
    label: 'Aguardando resposta',
    cls: 'bg-n-amber-3 text-n-amber-11',
  },
  completed: { label: 'Concluída', cls: 'bg-n-teal-3 text-n-teal-11' },
  failed: { label: 'Falhou', cls: 'bg-n-ruby-3 text-n-ruby-11' },
};
const sessionStatusLabel = s => SESSION_STATUS[s]?.label || s;
const sessionStatusClass = s =>
  SESSION_STATUS[s]?.cls || 'bg-n-slate-3 text-n-slate-11';

const fmtDateTime = dt => {
  if (!dt) return '—';
  const d = new Date(dt);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};
const answersText = answers => {
  const entries = Object.entries(answers || {});
  if (!entries.length) return '—';
  return entries.map(([k, v]) => `${k}: ${v}`).join(' · ');
};

// modal de relatorio de sessoes do flow
const report = ref({
  open: false,
  flow: null,
  sessions: [],
  stats: {},
  loading: false,
});
const openReport = async f => {
  report.value = {
    open: true,
    flow: f,
    sessions: [],
    stats: {},
    loading: true,
  };
  try {
    const { data } = await FlowsAPI.sessions(f.id);
    report.value.sessions = data.sessions || [];
    report.value.stats = data.stats || {};
  } catch (e) {
    useAlert('Erro ao carregar o relatório');
  } finally {
    report.value.loading = false;
  }
};
const closeReport = () => {
  report.value = {
    open: false,
    flow: null,
    sessions: [],
    stats: {},
    loading: false,
  };
};

// modal de disparo proativo (inicia o flow para uma audiencia)
const emptyDispatch = () => ({
  open: false,
  flow: null,
  inbox_id: '',
  audience_type: 'labels',
  label_ids: [],
  custom_filter_id: null,
  // previa do publico "Segmento" (spec 007): sem ela valida o disparo nao sai
  segment_preview: { valid: false, reachableCount: 0 },
  min_interval_seconds: 5,
  max_interval_seconds: 15,
  sending: false,
});
const dispatchModal = ref(emptyDispatch());

const openDispatch = f => {
  dispatchModal.value = { ...emptyDispatch(), open: true, flow: f };
};

const confirmDispatch = async () => {
  const m = dispatchModal.value;
  if (!m.inbox_id) {
    useAlert('Escolha o canal (caixa de entrada)');
    return;
  }
  if (m.audience_type === 'labels' && !m.label_ids.length) {
    useAlert('Escolha ao menos uma etiqueta');
    return;
  }
  if (m.audience_type === 'segment' && !m.segment_preview.valid) {
    useAlert(t('DISPARADOR.SEGMENT_AUDIENCE.INVALID'));
    return;
  }
  if (
    m.audience_type === 'all' &&
    !window.confirm(
      'Atenção: vai iniciar o flow para TODOS os contatos com telefone. Confirmar?'
    )
  )
    return;
  m.sending = true;
  try {
    await FlowsAPI.trigger(m.flow.id, {
      inbox_id: m.inbox_id,
      audience_type: m.audience_type,
      label_ids: m.label_ids,
      custom_filter_id: m.custom_filter_id,
      min_interval_seconds: m.min_interval_seconds,
      max_interval_seconds: m.max_interval_seconds,
    });
    useAlert('Disparo do flow iniciado!');
    dispatchModal.value = emptyDispatch();
    await fetchFlows();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao disparar o flow');
  } finally {
    m.sending = false;
  }
};

const accountId = () => store.getters.getCurrentAccountId;

const STATUS_LABELS = { draft: 'Rascunho', active: 'Ativo' };
const statusLabel = s => STATUS_LABELS[s] || s;

const triggerLabel = f => {
  const cfg = f.trigger_config || {};
  const nIn = (cfg.inbox_ids || []).length;
  const inboxInfo = nIn ? ` · ${nIn} caixa(s)` : ' · todas as caixas';
  if (cfg.type === 'message') {
    const kws = (cfg.keywords || []).join(', ');
    return (kws ? `Palavra-chave: ${kws}` : 'Qualquer mensagem') + inboxInfo;
  }
  return 'Manual / disparo' + inboxInfo;
};

const fetchFlows = async () => {
  loading.value = true;
  try {
    const { data } = await FlowsAPI.get();
    flows.value = data;
  } catch (e) {
    useAlert('Erro ao carregar flows');
  } finally {
    loading.value = false;
  }
};

const openEditor = id => {
  router.push({
    name: 'flows_editor',
    params: { accountId: accountId(), flowId: id },
  });
};

const createFlow = async () => {
  try {
    const payload = {
      flow: {
        name: 'Novo flow',
        status: 'draft',
        nodes: [
          {
            id: 'start',
            type: 'start',
            position: { x: 80, y: 80 },
            data: { label: 'Início' },
          },
        ],
        edges: [],
        trigger_config: { type: 'message', keywords: [] },
      },
    };
    const { data } = await FlowsAPI.create(payload);
    openEditor(data.id);
  } catch (e) {
    useAlert('Erro ao criar flow');
  }
};

const toggleActive = async f => {
  try {
    await FlowsAPI.update(f.id, {
      flow: { status: f.status === 'active' ? 'draft' : 'active' },
    });
    await fetchFlows();
  } catch (e) {
    useAlert('Erro ao alterar status');
  }
};

const removeFlow = async f => {
  if (!window.confirm(`Remover o flow "${f.name}"?`)) return;
  try {
    await FlowsAPI.delete(f.id);
    await fetchFlows();
  } catch (e) {
    useAlert('Erro ao remover');
  }
};

// A lista de Flows (kanban_flows) abre o flow anterior aqui com ?report=<id> ou ?dispatch=<id>;
// ao fechar o modal, volta para ela.
const backToFlows = () =>
  router.push({
    name: 'kanban_flows',
    params: { accountId: route.params.accountId },
  });
const openFromFlows = () => {
  const { report: reportId, dispatch } = route.query;
  const flow = flows.value.find(f => String(f.id) === (reportId || dispatch));
  if (!flow) return;
  if (reportId) openReport(flow);
  else openDispatch(flow);
};
watch(
  () => report.value.open,
  open => {
    if (!open && route.query.report) backToFlows();
  }
);
watch(
  () => dispatchModal.value.open,
  open => {
    if (!open && route.query.dispatch) backToFlows();
  }
);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  fetchFlows().then(openFromFlows);
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <header class="sticky top-0 z-10 px-6 bg-n-background">
      <div
        class="flex items-center justify-between w-full h-20 gap-2 max-w-5xl mx-auto"
      >
        <div class="flex flex-col">
          <span class="text-xl font-semibold tracking-[-0.02em] text-n-slate-12"
            >Flows</span
          >
          <span class="text-[13.5px] text-n-slate-10">
            Monte fluxos visuais (tipo typebot): mensagens, esperas, perguntas e
            ramificações.
          </span>
        </div>
        <Button
          label="Novo flow"
          icon="i-lucide-plus"
          size="sm"
          @click="createFlow"
        />
      </div>
    </header>

    <div class="flex-1 px-6 pb-8">
      <div class="flex flex-col gap-3 pt-1 max-w-5xl mx-auto w-full">
        <div
          v-for="f in flows"
          :key="f.id"
          class="flex flex-row items-center justify-between gap-4 px-6 py-5 border border-n-container rounded-2xl bg-n-solid-1"
        >
          <div
            class="flex flex-col gap-2 flex-1 min-w-0 cursor-pointer"
            @click="openEditor(f.id)"
          >
            <div class="flex items-center gap-3 min-w-0">
              <span class="text-base font-semibold text-n-slate-12 truncate">{{
                f.name
              }}</span>
              <span
                class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md shrink-0"
                :class="
                  f.status === 'active'
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{ statusLabel(f.status) }}
              </span>
            </div>
            <div
              class="flex items-center gap-4 text-sm text-n-slate-11 flex-wrap"
            >
              <span class="inline-flex items-center gap-1.5">
                <span class="i-lucide-zap size-4" /> {{ triggerLabel(f) }}
              </span>
              <span class="inline-flex items-center gap-1.5">
                <span class="i-lucide-users size-4" />
                {{ f.active_sessions_count }} sessões ativas
              </span>
            </div>
          </div>
          <div class="flex items-center gap-1.5 shrink-0">
            <Button
              label="Disparar"
              icon="i-lucide-send"
              variant="faded"
              color="blue"
              size="sm"
              @click="openDispatch(f)"
            />
            <Button
              :label="f.status === 'active' ? 'Pausar' : 'Ativar'"
              :icon="f.status === 'active' ? 'i-lucide-pause' : 'i-lucide-play'"
              variant="faded"
              :color="f.status === 'active' ? 'amber' : 'teal'"
              size="sm"
              @click="toggleActive(f)"
            />
            <Button
              icon="i-lucide-chart-column"
              variant="ghost"
              color="slate"
              size="sm"
              title="Relatório de sessões"
              @click="openReport(f)"
            />
            <Button
              icon="i-lucide-pencil"
              variant="ghost"
              color="slate"
              size="sm"
              title="Editar no canvas"
              @click="openEditor(f.id)"
            />
            <Button
              icon="i-lucide-trash-2"
              variant="ghost"
              color="ruby"
              size="sm"
              title="Remover"
              @click="removeFlow(f)"
            />
          </div>
        </div>

        <div
          v-if="!flows.length && !loading"
          class="flex flex-col items-center justify-center gap-3 py-24 text-center border border-n-container rounded-2xl bg-n-solid-1"
        >
          <span class="i-lucide-workflow size-10 text-n-slate-9" />
          <p class="text-base font-medium text-n-slate-12">Nenhum flow ainda</p>
          <p class="text-sm text-n-slate-11">
            Monte seu primeiro fluxo automático clicando em "Novo flow".
          </p>
          <Button
            label="Novo flow"
            icon="i-lucide-plus"
            size="sm"
            @click="createFlow"
          />
        </div>
      </div>
    </div>

    <!-- Modal: disparar flow para uma audiência -->
    <div
      v-if="dispatchModal.open"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      @click.self="dispatchModal = { ...dispatchModal, open: false }"
    >
      <div
        class="w-full max-w-md flex flex-col gap-4 rounded-2xl bg-n-solid-1 border border-n-container shadow-xl p-5"
      >
        <div>
          <h2 class="text-lg font-semibold tracking-[-0.02em] text-n-slate-12">
            Disparar flow — {{ dispatchModal.flow?.name }}
          </h2>
          <p class="text-sm text-n-slate-10">
            Inicia o fluxo para cada contato da audiência, com intervalo entre
            eles.
          </p>
        </div>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">Canal (caixa de entrada)</span>
          <select
            v-model="dispatchModal.inbox_id"
            class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
          >
            <option value="">Selecione...</option>
            <option v-for="i in inboxes" :key="i.id" :value="i.id">
              {{ i.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">Público-alvo</span>
          <select
            v-model="dispatchModal.audience_type"
            class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
          >
            <option value="labels">Por etiqueta</option>
            <option value="all">Todos os contatos (com telefone)</option>
            <option value="segment">
              {{ t('DISPARADOR.SEGMENT_AUDIENCE.OPTION') }}
            </option>
          </select>
        </label>

        <SegmentAudiencePicker
          v-if="dispatchModal.audience_type === 'segment'"
          v-model="dispatchModal.custom_filter_id"
          @result="dispatchModal.segment_preview = $event"
        />

        <div
          v-if="dispatchModal.audience_type === 'labels'"
          class="flex flex-wrap gap-2"
        >
          <label
            v-for="l in labels"
            :key="l.id"
            class="flex items-center gap-1 text-sm text-n-slate-11"
          >
            <input
              v-model="dispatchModal.label_ids"
              type="checkbox"
              :value="l.id"
            />
            {{ l.title }}
          </label>
          <p v-if="!labels.length" class="text-xs text-n-slate-10">
            Nenhuma etiqueta cadastrada.
          </p>
        </div>
        <p
          v-else-if="dispatchModal.audience_type === 'all'"
          class="text-xs text-n-amber-11"
        >
          ⚠️ Vai iniciar o flow para TODOS os contatos com telefone. Use com
          cuidado.
        </p>

        <div class="flex gap-3">
          <label class="flex flex-col gap-1 flex-1">
            <span class="text-sm text-n-slate-11">Intervalo mín (s)</span>
            <input
              v-model.number="dispatchModal.min_interval_seconds"
              type="number"
              min="0"
              class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
            />
          </label>
          <label class="flex flex-col gap-1 flex-1">
            <span class="text-sm text-n-slate-11">Intervalo máx (s)</span>
            <input
              v-model.number="dispatchModal.max_interval_seconds"
              type="number"
              min="1"
              class="h-10 px-3 rounded-lg outline outline-1 -outline-offset-1 outline-n-weak bg-n-background text-sm text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 transition-all"
            />
          </label>
        </div>

        <div class="flex gap-3 justify-end">
          <button
            class="px-4 h-10 text-sm font-medium rounded-lg text-n-slate-11 hover:bg-n-slate-2 transition-colors"
            @click="dispatchModal = { ...dispatchModal, open: false }"
          >
            Cancelar
          </button>
          <button
            class="px-4 h-10 text-sm font-semibold text-white bg-n-blue-9 rounded-lg hover:bg-n-blue-10 disabled:opacity-50 transition-colors"
            :disabled="dispatchModal.sending"
            @click="confirmDispatch"
          >
            Disparar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: relatório de sessões do flow -->
    <div
      v-if="report.open"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      @click.self="closeReport"
    >
      <div
        class="w-full max-w-4xl max-h-[85vh] flex flex-col rounded-2xl bg-n-solid-1 shadow-xl border border-n-container overflow-hidden"
      >
        <div class="flex items-start justify-between gap-3 px-6 pt-5 pb-4">
          <div class="flex items-center gap-3 min-w-0">
            <span
              class="grid size-10 place-content-center rounded-[10px] bg-n-blue-3 shrink-0"
            >
              <span class="i-lucide-workflow size-5 text-n-blue-9" />
            </span>
            <div class="flex flex-col min-w-0">
              <h2
                class="text-base font-semibold tracking-[-0.02em] text-n-slate-12 truncate"
              >
                Relatório — {{ report.flow?.name }}
              </h2>
              <span class="text-xs text-n-slate-10"
                >Execuções do flow por contato</span
              >
            </div>
          </div>
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="sm"
            @click="closeReport"
          />
        </div>

        <div class="grid grid-cols-4 gap-3 px-6 pb-4">
          <div
            class="flex flex-col items-center gap-0.5 rounded-xl bg-n-blue-3 py-3"
          >
            <span class="text-xl font-semibold text-n-blue-11">{{
              report.stats.active || 0
            }}</span>
            <span class="text-xs text-n-slate-11">Em andamento</span>
          </div>
          <div
            class="flex flex-col items-center gap-0.5 rounded-xl bg-n-amber-3 py-3"
          >
            <span class="text-xl font-semibold text-n-amber-11">{{
              report.stats.waiting_input || 0
            }}</span>
            <span class="text-xs text-n-slate-11">Aguardando</span>
          </div>
          <div
            class="flex flex-col items-center gap-0.5 rounded-xl bg-n-teal-3 py-3"
          >
            <span class="text-xl font-semibold text-n-teal-11">{{
              report.stats.completed || 0
            }}</span>
            <span class="text-xs text-n-slate-11">Concluídas</span>
          </div>
          <div
            class="flex flex-col items-center gap-0.5 rounded-xl bg-n-ruby-3 py-3"
          >
            <span class="text-xl font-semibold text-n-ruby-11">{{
              report.stats.failed || 0
            }}</span>
            <span class="text-xs text-n-slate-11">Falhas</span>
          </div>
        </div>

        <div class="flex-1 overflow-auto border-t border-n-slate-3">
          <div
            v-if="report.loading"
            class="flex items-center justify-center gap-2 p-10 text-sm text-n-slate-11"
          >
            <span class="i-lucide-loader-circle size-4 animate-spin" />
            Carregando...
          </div>
          <table v-else class="w-full text-sm">
            <thead class="sticky top-0 bg-n-slate-2 z-10">
              <tr
                class="text-left text-[11px] uppercase tracking-[0.04em] font-semibold text-n-slate-9"
              >
                <th class="px-6 py-3">Contato</th>
                <th class="px-3 py-3">Etapa atual</th>
                <th class="px-3 py-3">Status</th>
                <th class="px-3 py-3">Respostas</th>
                <th class="px-6 py-3 text-right">Última atividade</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="s in report.sessions"
                :key="s.id"
                class="border-t border-n-slate-3 hover:bg-n-slate-2 transition-colors"
              >
                <td class="px-6 py-3">
                  <div class="flex flex-col">
                    <span class="font-medium text-n-slate-12">{{
                      s.contact_name || 'Sem nome'
                    }}</span>
                    <span class="text-xs text-n-slate-10">{{
                      s.contact_phone
                    }}</span>
                  </div>
                </td>
                <td
                  class="px-3 py-3 text-n-slate-11 max-w-[200px] truncate"
                  :title="s.current_step_label"
                >
                  {{ s.current_step_label }}
                </td>
                <td class="px-3 py-3">
                  <span
                    class="text-xs font-medium inline-flex items-center h-6 px-2 rounded-md"
                    :class="sessionStatusClass(s.status)"
                  >
                    {{ sessionStatusLabel(s.status) }}
                  </span>
                </td>
                <td
                  class="px-3 py-3 text-xs text-n-slate-11 max-w-[220px] truncate"
                  :title="answersText(s.answers)"
                >
                  {{ answersText(s.answers) }}
                </td>
                <td
                  class="px-6 py-3 text-xs text-n-slate-11 text-right whitespace-nowrap"
                >
                  {{ fmtDateTime(s.last_activity_at) }}
                </td>
              </tr>
              <tr v-if="!report.sessions.length">
                <td
                  colspan="5"
                  class="px-6 py-12 text-center text-sm text-n-slate-11"
                >
                  Nenhuma execução ainda. As sessões aparecem quando um contato
                  entra no flow.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
</template>
